import { CSSProperties, useCallback, ReactElement } from 'react'
import { styled, Typography } from '@mui/material'
import { RawFileMessage } from '@weblite/ts-action/message'
import { getFileByUrl, getFileUrl, openFile } from '@weblite/file'
import { ActionHandler } from '$common/action-handler'
import { FileMessageIcon } from '$common/icons/file-message'
import { useContentFileStatus } from '$common/react-hooks/message'
import { convertBlobToFile } from '$comp/_app/components/chat/components/body/components/messageList/messages/message-bodies/file/helpet'
import { MessageMenuIcon } from '../../components/menu-icon'
import { sendFileToTwa } from '$logic/twaCommunication/service'
import { TWA_API_SUPPORT } from '$logic/twaCommunication'
import { IS_IOS, isInTwa } from '$func/environmentInfo'
import {
  convertExtensionToMimeType,
  formatFileSize,
  getFileExtension,
  getFileTitle,
} from '$func/file'
import { isInWebView } from '$func/environmentInfo'

const Container = styled('div')`
  display: flex;
  position: relative;
  align-items: center;

  padding: 10px 10px 10px 32px;
  box-sizing: border-box;
  overflow: hidden;
`

const TextsContainer = styled('div')`
  flex-grow: 1;
  /* @noflip */
  margin-right: 10px;
  overflow: hidden;
`

const FileMetaTypography = styled(Typography)(({ theme }) => ({
  display: 'inline-block',
  fontSize: 11,
  color: theme.palette.gray,
  lineHeight: '12px',
}))

export function FileComponenet({
  attachment,
  statusComp,
  uploadId,
  rootStyle,
  iid,
  roomId,
  id,
}: {
  attachment: RawFileMessage['attachment']
  statusComp: ReactElement | null
  uploadId: string
  rootStyle?: CSSProperties
  iid: number
  roomId: string
  id: string
}) {
  const { fileId, filename, size } = attachment

  const { state, percentage, uploadedBytes, cancelOperation, startDownload } =
    useContentFileStatus({
      fileId,
      uploadId,
      messageType: 'FILE',
      messageId: id,
      filename,
    })

  const isInProgress = state === 'UPLOADING' || state === 'DOWNLOADING'

  const { sizeText: fileSizeText, unit: fileSizeUnit } = formatFileSize(size)
  const { sizeText: progressedSizeText } =
    state === 'UPLOADING'
      ? formatFileSize(uploadedBytes)
      : formatFileSize(
          Math.round(size * (percentage / 100)),
          fileSizeUnit,
          false,
        )

  const fileTitle = getFileTitle(filename)
  const fileExtension = getFileExtension(filename).toUpperCase()

  const onOpenFile = useCallback(() => {
    if (TWA_API_SUPPORT.OPEN_FILE && isInWebView)
      AndroidBridge?.runFile(
        fileId,
        getFileUrl(fileId),
        filename,
        convertExtensionToMimeType(fileExtension),
      )
    else openFile(getFileUrl(fileId), filename, IS_IOS)
  }, [fileId, filename])

  return (
    <>
      <Container sx={{ width: '280px', ...rootStyle }}>
        <ActionHandler
          state={state}
          percentage={percentage}
          onCancelClick={cancelOperation}
          onDownloadClick={startDownload}
          onCustomActionIconClick={onOpenFile}
          customActionIconComp={
            <FileMessageIcon
              sx={{ color: 'white', width: '24px', height: '24px' }}
            />
          }
        />

        <TextsContainer>
          <Typography
            color="semiBlack"
            variant="caption1"
            sx={{
              display: 'block',
              marginBottom: '8px',
            }}
            noWrap
          >
            {fileTitle}
          </Typography>

          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              overflow: 'hidden',
            }}
          >
            <FileMetaTypography>{fileExtension}</FileMetaTypography>

            <FileMetaTypography
              sx={{
                color: '#c4c4c4',
                margin: '0 4px',
              }}
            >
              /
            </FileMetaTypography>

            <FileMetaTypography
              noWrap
              sx={{
                color: isInProgress ? 'primary.main' : 'gray',
              }}
            >
              {isInProgress
                ? `${progressedSizeText} از ${fileSizeText}`
                : fileSizeText}
            </FileMetaTypography>
          </div>
        </TextsContainer>

        <MessageMenuIcon iid={iid} roomId={roomId} />
      </Container>
      {statusComp}
    </>
  )
}

FileComponenet.defaultProps = {
  rootStyle: null,
}
